import * as React from 'react';
import {inject as mobxInject, IReactComponent, observer, Provider} from 'mobx-react';
import {Store} from './stores/store';
import DashboardStore from './stores/DashboardStore';
import InsightsTableStore from './stores/InsightsTableStore';
import MemoryBarStore from './stores/MemoryBarStore';
import TimeIntervalStore from './stores/TimeIntervalStore';
import {TranslationFunction} from 'i18next';
import {t} from './i18n/i18n';
import ReportStore from './report/stores/ReportStore';
import ReportEditorStore from './report/stores/ReportEditorStore';
import SearchSelectorTableStore from './stores/searchterm/SearchSelectorTableStore';
import ProductSelectorTableStore from './stores/product/ProductSelectorTableStore';
import CampaignSelectorTableStore from './stores/campaign/CampaignSelectorTableStore';
import ProductLineDiagramStore from './stores/product/ProductLineDiagramStore';
import SearchLineDiagramStore from './stores/searchterm/SearchLineDiagramStore';
import CampaignLineDiagramStore from './stores/campaign/CampaignLineDiagramStore';

interface StoreMapping {
    lineDiagramStore: ProductLineDiagramStore | SearchLineDiagramStore | CampaignLineDiagramStore;
    store: Store;
    insightsTableStore: InsightsTableStore;
    dashboardStore: DashboardStore;
    memoryBarStore: MemoryBarStore;
    timeIntervalStore: TimeIntervalStore;
    reportStore: ReportStore;
    reportEditorStore: ReportEditorStore;
    selectorTableStore: SearchSelectorTableStore | ProductSelectorTableStore | CampaignSelectorTableStore;
    t: TranslationFunction;
}

/**
 * Injects stores to a component. We wrap mobx to get type safety.
 *
 * // Usage:
 * interface MyProps { // all non store props
 *     name: string;
 *     enabled: boolean;
 * }
 *
 * @observer
 * class MyComponent extends React.Component<MyProps & Stores<"timeIntervalStore">> {
 *     public render() {
 *         return <div>{this.props.timeIntervalStore.something}</div>
 *     }
 * }
 *
 * export default inject('timeIntervalStore')(MyComponent);
 *
 *
 * // other component
 * const Abc = () => {
 *     return <MyComponent name="abc" enabled={true} />
 * }
 *
 */
export const inject = <Keys extends keyof StoreMapping>(
    ...stores: Keys[]
): (<Props>(comp: IReactComponent<Props>) => IReactComponent<Pick<Props, Exclude<keyof Props, Keys>>>) => {
    return mobxInject((allStores: {store: Store}) => {
        return stores.reduce((merged: {}, store: Keys) => {
            if (store === 'store') {
                return {...merged, store: allStores.store};
            } else if (store === 't') {
                return {...merged, t};
            } else {
                return {...merged, [store]: allStores.store[store as Exclude<Keys, 'store' | 't'>]};
            }
        }, {});
    });
};

export type Stores<S extends keyof StoreMapping> = Pick<StoreMapping, S>;

export const StoreProvider = observer(({store, children}: {store: Store; children: React.ReactNode}) => {
    return <Provider store={store}>{children}</Provider>;
});



// WEBPACK FOOTER //
// ./src/inject.tsx